import React, { memo, forwardRef, useState, useCallback, useEffect, useLayoutEffect, useRef, useContext, useMemo, } from 'react';
import cn from 'classnames';
import styles from './styles.module.css';
import { useForwardedRef } from '../../hooks';
export const TransitionGroupContext = React.createContext(null);
export const TransitionGroup = function TransitionGroup({ children, on }) {
    const [mounted, setMounted] = useState(on);
    const transitionsState = useRef(new Map());
    useLayoutEffect(() => {
        if (!mounted && on) {
            setMounted(true);
        }
    }, [on, mounted]);
    const registerTransition = useCallback((transition) => {
        transitionsState.current.set(transition, true);
        return () => {
            transitionsState.current.delete(transition);
        };
    }, []);
    const onTransitionLeave = useCallback((transition) => {
        transitionsState.current.set(transition, false);
        if (Array.from(transitionsState.current.values()).every(x => !x)) {
            setMounted(false);
        }
    }, []);
    const context = useMemo(() => ({
        on,
        mounted,
        registerTransition,
        onTransitionLeave,
    }), [registerTransition, on, mounted]);
    return (React.createElement(TransitionGroupContext.Provider, { value: context }, children));
};
export const Transition = memo(forwardRef(function Transition({ on, duration = 500, className, children, keepMount }, ref) {
    const childrenCount = React.Children.count(children);
    if (childrenCount !== 1) {
        throw new Error('Transition component should have exactly one child');
    }
    const transition = useRef({});
    const context = useContext(TransitionGroupContext);
    const timerRef = useRef(0);
    const leaveTimerRef = useRef(0);
    const elRef = useForwardedRef(ref);
    const finalState = on || (context === null || context === void 0 ? void 0 : context.on) || false;
    // всегда стартует с false чтобы анимация проигрывалась
    // даже когда компонент замаунчен и уже в состоянии on
    const [onInner, setOnInner] = useState(false);
    const onTransitionEnd = useCallback((e, onInner, finalState) => {
        if (e.target !== elRef.current)
            return;
        clearTimeout(leaveTimerRef.current);
        if (!onInner && !finalState) {
            context === null || context === void 0 ? void 0 : context.onTransitionLeave(transition.current);
        }
    }, []);
    useEffect(() => {
        return context === null || context === void 0 ? void 0 : context.registerTransition(transition.current);
    }, []);
    useEffect(() => {
        if (finalState && !onInner) {
            cancelAnimationFrame(timerRef.current);
            timerRef.current = requestAnimationFrame(() => {
                setOnInner(true);
            });
        }
        if (!finalState && onInner) {
            cancelAnimationFrame(timerRef.current);
            timerRef.current = requestAnimationFrame(() => {
                setOnInner(false);
                // не во всех браузерах гарантированно отрабатывает евент onTransitionEnd
                clearTimeout(leaveTimerRef.current);
                leaveTimerRef.current = window.setTimeout(() => {
                    onTransitionEnd({ target: elRef.current }, false, finalState);
                }, 
                // даем времени с запасом, на случай тормозов
                duration + 50);
            });
        }
    }, [finalState, onInner]);
    useEffect(() => () => {
        clearTimeout(leaveTimerRef.current);
        cancelAnimationFrame(timerRef.current);
    }, []);
    if (!!context && context.mounted === false && !keepMount) {
        return null;
    }
    return React.cloneElement(children, {
        ...children.props,
        onTransitionEnd: (e) => onTransitionEnd(e, onInner, finalState),
        ref: elRef,
        style: {
            ...children.props.style,
            ['--duration']: `${duration}ms`,
        },
        className: cn(children.props.className, className, {
            [styles.on]: onInner,
        }),
    });
}));
export const Opacity = memo(forwardRef(function Opacity({ children, ...rest }, ref) {
    return (React.createElement(Transition, { ...rest, className: styles.opacity, ref: ref }, children));
}));
export const Slide = memo(forwardRef(function Slide({ children, direction, ...rest }, ref) {
    return (React.createElement(Transition, { ...rest, className: cn(styles.slide, styles[direction]), ref: ref }, children));
}));
